import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ElementRef,
  ViewChild,
  HostListener,
  NgZone,
  OnDestroy,
} from '@angular/core';

import {
  ColumnReorderEvent,
  ColumnResizeArgs,
  ColumnVisibilityChangeEvent,
  ExpandEvent,
} from '@progress/kendo-angular-treelist';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import _ from 'lodash';
import { formatDate } from '@angular/common';
import { getReadableKendoOperator } from '@stockaid/utils';

import { HeaderService } from 'src/app/core/services/header.service';
import { toggleFullScreen } from '../../full-screen/toggle-full-screen';
import { BaseComponent } from 'src/app/core/infrastructure/classes/base-component';
import { GridState } from 'src/app/core/infrastructure/classes/grid-state';
import { GridStateColumn } from 'src/app/core/infrastructure/classes/grid-state-column';
import { Logic } from 'src/app/core/infrastructure/enums/logic.enum';
import { TreelistLevelConfig } from './treelist-level-config.interface';
import { TreelistColumnDef } from './treelist-column-def.interface';
import { TreelistCustomCellDirective } from './cells/treelist-custom-cell.directive';
import { GridStatePagination } from 'src/app/core/infrastructure/classes/grid-state-pagination';
import { RichTooltipConfig } from '../rich-tooltip.interface';

export const LOADING_ROW_TYPE = '__loading__';

/**
 * Shape reported by Kendo TreeList's `columnLockedChange` output.
 * `ColumnLockedChangeEvent` is not publicly exported by the package, so we
 * declare the minimal structural type we actually consume.
 */
interface ColumnLockedChangeEvent {
  columns: Array<{ field: string; locked: boolean }>;
}

/** Specific shape for inline Kendo visibility events */
interface KendoColVisibilityEvent {
  hidden: boolean;
}

/** Specific shape for inline Kendo locked events */
interface KendoColLockedEvent {
  locked: boolean;
}

export type TreeKey = string | number;

export interface TreeSelection {
  id: TreeKey;
  level: number;
  parentId?: TreeKey;
}

/** Internal wrapper for items injected with hierarchy state. */
export type InternalTreeItem<T> = T & {
  __depth?: number;
  __type?: string;
  __parentId?: TreeKey;
  children?: InternalTreeItem<T>[];
};

@Component({
  selector: 'app-forecast-rxtreelist',
  templateUrl: './forecast-rxtreelist.component.html',
  styleUrls: ['./forecast-rxtreelist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForecastRxTreelistComponent<T = Record<string, unknown>> extends BaseComponent implements AfterContentInit, AfterViewInit, OnDestroy {

  /** Default empty filter used when no saved state or on clear. */
  private static readonly EMPTY_FILTER: CompositeFilterDescriptor = {
    logic: Logic.and,
    filters: [],
  };

  public rootItemsList: InternalTreeItem<T>[] = [];

  /** Current page of root-level rows (server provides just this page). */
  @Input() set rootItems(value: T[]) {
    this.rootItemsList = (value ?? []).map(item => ({ ...item, __depth: 0, children: [] } as InternalTreeItem<T>));

    this.nodeMap.clear();
    this.loadedParentIds.clear();
    this.expandedIds.clear();
    this.rootItemsList.forEach(item => this.nodeMap.set(this.getItemId(item), item));
    this.cdr.markForCheck();
  }

  /** Total root item count from count API — drives the pager display. */
  @Input() totalRootCount: number = 0;

  /** Current root-level page size. */
  @Input() pageSize: number = 20;

  /** Current root-level skip. */
  @Input() skip: number = 0;

  /**
   * Declares each expandable level below root (0-indexed from root's children).
   * levelConfigs[0] = first child level, levelConfigs[1] = grandchild level, etc.
   * When empty, the tree renders as a flat non-expandable list.
   */
  @Input() levelConfigs: TreelistLevelConfig[] = [];

  /** Page size options shown in the pager dropdown. */
  readonly pageSizes: number[] = [10, 20, 50, 100];

  // ─── Column & identity inputs ─────────────────────────────────────────────

  @Input() set columns(value: TreelistColumnDef[]) {
    this.columnDefs = value || [];
    this.applyColumnStateFromSaved();
  }
  @Input() idField: string = 'id';
  
  /**
   * Array of ID field names by depth level.
   * Index 0 = root level (depth 0), index 1 = first child level (depth 1), etc.
   * Example: ['itemKey', 'adId'] means depth 0 uses 'itemKey', depth 1 uses 'adId'
   * Falls back to idField if not provided or depth exceeds array length.
   */
  @Input() idFieldsByDepth: string[] = [];

  // ─── UI inputs ────────────────────────────────────────────────────────────

  @Input() loading: boolean = false;
  @Input() height: number = 600;
  @Input() disabledCalcGridHeight: boolean = false;
  public gridHeight: number = 600;

  // ─── Grid State input (hydrates sort / filter / per-column visible + locked) ──

  @Input() set initialGridState(value: GridState) {
    if (!value) return;
    this.savedGridState = value;
    this.treeListSort = value.sort ?? [];
    this.treeListFilter = value.filter ?? { ...ForecastRxTreelistComponent.EMPTY_FILTER };
    this.applyColumnStateFromSaved();
    this.cdr.markForCheck();
  }

  @Input() fullScreenIconIsVisible: boolean = true;
  @Input() toogleFullScreenIsVisible: boolean = true;
  @Input() showCheckboxColumn: boolean = true;
  @Input() showToolbar: boolean = true;
  @Input() checkboxTooltip: string = '';
  
  public activeRichTooltip: RichTooltipConfig | null = null;

  /**
   * Kendo TreeList checkable settings for cascading selection.
   * When provided, enables parent-child checkbox cascading behavior.
   */
  @Input() checkableSettings?: { checkChildren?: boolean; checkParents?: boolean };

  /**
   * Optional predicate to restrict cascade selection to specific children.
   * When `checked=true`, only children for which this returns `true` are selected.
   * Deselection always cascades to all children regardless of this filter.
   */
  @Input() checkChildFilter: ((child: T) => boolean) | null = null;

  /**
   * Optional predicate to disable selection for specific rows.
   * If returns true, the checkbox for that row will be disabled and it won't appear selected.
   */
  @Input() rowSelectionDisabled?: (dataItem: T, level: number) => boolean;

  @HostListener('window:resize')
  public onResize(): void {
    this.calcGridHeight();
  }


  // ─── Outputs ──────────────────────────────────────────────────────────────

  /** Fired on root pager navigation. Parent updates [rootItems] + [skip]. */
  @Output() pageChange = new EventEmitter<{ skip: number; take: number }>();

  /** Informational — fired when user collapses a node. */
  @Output() nodeCollapse = new EventEmitter<{ dataItem: T }>();

  /** Fired whenever filter / sort / visibility / locked changes so the parent can persist. */
  @Output() stateChange = new EventEmitter<GridState>();

  /**
   * Fired specifically when sort or filter changes — signals the parent to reload server-side data.
   * Carries the new sort and filter so the parent can update its query params before fetching.
   */
  @Output() sortFilterChange = new EventEmitter<{ sort: SortDescriptor[]; filter: CompositeFilterDescriptor }>();

  // ─── Row selection (exposed for parent @ViewChild access) ────────────────

  public selectedKeys: TreeSelection[] = [];
  public selectedItems: T[] = [];
  public isSelectAll: boolean = false;
  public excludeIds: TreeSelection[] = [];

  public get hasSelection(): boolean {
    return this.isSelectAll || this.selectedKeys.length > 0;
  }

  public clearSelection(): void {
    this.selectedKeys = [];
    this.selectedItems = [];
    this.isSelectAll = false;
    this.excludeIds = [];
    this.cdr.markForCheck();
  }

  public get isFilter(): boolean {
    return (
      (this.treeListFilter?.filters?.length > 0) ||
      (this.treeListSort?.length > 0)
    );
  }


  // ─── DOM References ──────────────────────────────────────────────────────
  @ContentChildren(TreelistCustomCellDirective, { descendants: true })
  customCells!: QueryList<TreelistCustomCellDirective>;
  customCellTemplates = new Map<string, TemplateRef<any>>();

  @ViewChild('treeListView', { static: false, read: ElementRef }) treeListView!: ElementRef;
  
  // ── Hover Sync Logic (High Performance) ────────────────────────────────
  private setupHoverSync(): void {
    this.zone.runOutsideAngular(() => {
      const el = this.elRef.nativeElement;
      el.addEventListener('mouseover', this.handleHoverSync);
      el.addEventListener('mouseout', this.handleHoverSync);
    });
  }

  private removeHoverSync(): void {
    const el = this.elRef.nativeElement;
    el.removeEventListener('mouseover', this.handleHoverSync);
    el.removeEventListener('mouseout', this.handleHoverSync);
  }

  private handleHoverSync = (event: MouseEvent): void => {
    const isOver = event.type === 'mouseover';
    const target = event.target as HTMLElement;
    const row = target.closest('tr');
    
    // Safety check: is it a data row and inside our component?
    if (!row || row.closest('thead') || !this.elRef.nativeElement.contains(row)) return;

    const rowIndex = row.sectionRowIndex;
    const tables = this.elRef.nativeElement.querySelectorAll('.k-grid-container table, .k-grid-content-locked table');
    
    tables.forEach((table: HTMLTableElement) => {
      const targetRow = table.rows[rowIndex];
      if (targetRow) {
        if (isOver) {
          targetRow.classList.add('sync-hover');
        } else {
          targetRow.classList.remove('sync-hover');
        }
      }
    });
  }


  // ─── Private state ────────────────────────────────────────────────────────

  /** O(1) lookup map to eliminate recursive tree searching. */
  public nodeMap = new Map<TreeKey, InternalTreeItem<T>>();

  /** Tracks which parent nodes have already had their children loaded. */
  private loadedParentIds = new Set<TreeKey>();

  /** Tracks expansion state manually (required for HierarchyBinding + OnPush). */
  private expandedIds = new Set<TreeKey>();

  /** Current column definitions injected by Parent. */
  public columnDefs: TreelistColumnDef[] = [];

  /** Live sort state bound to <kendo-treelist>. */
  public treeListSort: SortDescriptor[] = [];

  /** Live filter state bound to <kendo-treelist>. */
  public treeListFilter: CompositeFilterDescriptor = {
    ...ForecastRxTreelistComponent.EMPTY_FILTER,
  };

  /** Last persisted grid state (used to re-apply on late `columns` input arrival). */
  private savedGridState: GridState | null = null;

  /** Expose for template loading row check. */
  public readonly LOADING_ROW_TYPE = LOADING_ROW_TYPE;

  /** Returns a CSS class for each row to differentiate hierarchy. */
  public getRowClass = (args: { dataItem: InternalTreeItem<T> }): string => {
    const item = args.dataItem;
    const depth = item.__depth ?? 0;
    const isParent = depth === 0;
    
    return [
      isParent ? 'treelist-row-parent' : 'treelist-row-child',
      item.__type === LOADING_ROW_TYPE ? 'treelist-row-loading' : ''
    ].join(' ');
  };

  public isFullScreen: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private headerService: HeaderService,
    private elRef: ElementRef,
    private zone: NgZone,
  ) {

    super();
    this.gridHeight = this.height;
  }

  ngAfterContentInit() {
    this.customCells.forEach(cell => {
      this.customCellTemplates.set(cell.columnName, cell.template);
    });
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    setTimeout(() => this.calcGridHeight(), 250);
    this.setupHoverSync();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.removeHoverSync();
  }


  // ─── Expand / Collapse ────────────────────────────────────────────────────

  public isExpanded = (dataItem: InternalTreeItem<T>): boolean =>
    this.expandedIds.has(this.getItemId(dataItem));

  /**
   * Determines which rows show an expand arrow.
   * - Loading rows: never expandable.
   * - Nodes at max depth (no further LevelConfig): never expandable.
   * - Otherwise: delegates to LevelConfig.hasChildren, defaulting to true.
   */
  public hasChildren = (dataItem: InternalTreeItem<T>): boolean => {
    if (dataItem.__type === LOADING_ROW_TYPE) return false;
    const depth: number = dataItem.__depth ?? 0;
    const nextConfig = this.levelConfigs[depth];
    if (!nextConfig) return false;
    return nextConfig.hasChildren ? nextConfig.hasChildren(dataItem as T) : true;
  };

  public fetchChildrenCallback = (item: InternalTreeItem<T>): InternalTreeItem<T>[] => {
    return item.children || [];
  };

  public onExpand(args: ExpandEvent): void {
    const dataItem = args.dataItem as InternalTreeItem<T>;
    const parentId = this.getItemId(dataItem);
    this.expandedIds.add(parentId);

    // Children already loaded — re-render only, no fetch needed.
    if (this.loadedParentIds.has(parentId)) {
      // Still need to check if parent is selected and cascade to already-loaded children
      if (this.checkableSettings?.checkChildren) {
        const parentSelected = this.isRowSelected(dataItem as T);
        if (parentSelected) {
          this.cascadeSelectionToChildren(dataItem as T, true);
          this.cdr.markForCheck();
        }
      }
      return;
    }

    const depth: number = dataItem.__depth ?? 0;
    const config = this.levelConfigs[depth];
    if (!config) return;

    this.setChildren(parentId, [this.createLoadingRow(parentId)]);

    config.fetch(parentId)
      .pipe(this.autoCleanUp())
      .subscribe(items => {
        this.setChildren(parentId, items as T[]);
        this.loadedParentIds.add(parentId);
        
        // After loading children, check if parent is selected and cascade selection
        if (this.checkableSettings?.checkChildren) {
          const parentSelected = this.isRowSelected(dataItem as T);
          if (parentSelected) {
            this.cascadeSelectionToChildren(dataItem as T, true);
            this.cdr.markForCheck();
          }
        }
      });
  }

  public onCollapse(args: ExpandEvent): void {
    const dataItem = args.dataItem as InternalTreeItem<T>;
    this.expandedIds.delete(this.getItemId(dataItem));
    this.nodeCollapse.emit({ dataItem: args.dataItem as T });
  }

  // ─── Pagination ───────────────────────────────────────────────────────────

  public onPageChange(event: { skip: number; take: number }): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.loadedParentIds.clear();
    this.expandedIds.clear();
    this.nodeMap.clear();
    this.pageChange.emit({ skip: event.skip, take: event.take });
    this.emitGridState();
    this.cdr.markForCheck();
  }

  // ─── External actions (called by parent via @ViewChild) ──────────────────

  public applyExternalFilter(filter: CompositeFilterDescriptor): void {
    this.treeListFilter = filter;
    this.emitGridState();
    this.cdr.markForCheck();
  }

  /**
   * Patches specific properties for items in the nodeMap in place.
   * This is a generic way to update local UI state without a full rootItems reset.
   * @param ids Array of item identifiers (itemKey for parents, adId for children)
   * @param patch Partial object containing the fields to update
   */
  public patchItemData(ids: (string | number)[], patch: Partial<T>): void {
    let changed = false;

    ids.forEach(id => {
      const item = this.nodeMap.get(id);
      if (item) {
        // Apply each key in the patch to the item
        Object.keys(patch).forEach(key => {
          if ((item as any)[key] !== (patch as any)[key]) {
            (item as any)[key] = (patch as any)[key];
            changed = true;
          }
        });
      }
    });

    if (changed) {
      this.cdr.markForCheck();
    }
  }

  /**
   * Unique identifier for TreeList rows to optimize rendering performance.
   */
  public trackByFn = (index: number, item: any): any => {
    // If idFieldsByDepth is provided, use it based on __depth
    if (this.idFieldsByDepth && item.__depth !== undefined && this.idFieldsByDepth[item.__depth]) {
      const field = this.idFieldsByDepth[item.__depth];
      if (item[field] !== undefined) {
        return item[field];
      }
    }

    // Priority: idField input, then fallback to itemKey/adId heuristics
    if (this.idField && item[this.idField] !== undefined) {
      return item[this.idField];
    }
    return item.itemKey || item.adId || index;
  }

  // ─── Row selection ────────────────────────────────────────────────────────

  public onSelectAllChange(checked: boolean): void {
    this.isSelectAll = checked;
    this.excludeIds = [];
    this.selectedKeys = [];
    this.selectedItems = [];
    this.cdr.markForCheck();
  }

  public onHeaderTooltipEnter(col: TreelistColumnDef): void {
    this.activeRichTooltip = col.richTooltip || null;
  }

  public onRowSelectChange(dataItem: T, checked: boolean): void {
    // Perform direct toggle
    this.toggleItemSelection(dataItem, checked);
    
    // Cascading selection: if checkableSettings.checkChildren is true, cascade to children
    if (this.checkableSettings?.checkChildren) {
      this.cascadeSelectionToChildren(dataItem, checked);
    }

    // Cascading selection: if checkableSettings.checkParents is true, cascade to parents
    if (this.checkableSettings?.checkParents) {
      this.cascadeSelectionToParents(dataItem, checked);
    }
    
    this.cdr.markForCheck();
  }

  /**
   * Updates selection state for a single item (either selectedKeys or excludeIds).
   */
  private toggleItemSelection(dataItem: T, checked: boolean): void {
    const internalItem = dataItem as InternalTreeItem<T>;
    const id = this.getItemId(dataItem);
    const depth = (dataItem as any).__depth ?? 0;

    if (this.isSelectAll) {
      if (checked) {
        this.excludeIds = this.excludeIds.filter(e => e.id !== id);
      } else if (!this.excludeIds.some(e => e.id === id)) {
        this.excludeIds = [...this.excludeIds, { id, level: depth, parentId: internalItem.__parentId }];
      }
      console.log(this.excludeIds);
    } else {
      if (checked) {
        if (!this.selectedKeys.some(e => e.id === id && e.level === depth)) {
          this.selectedKeys = [...this.selectedKeys, { id, level: depth, parentId: internalItem.__parentId }];
          this.selectedItems = [...this.selectedItems, dataItem];
        }
      } else {
        this.selectedKeys = this.selectedKeys.filter(k => !(k.level === depth && k.id === id));
        this.selectedItems = this.selectedItems.filter(item => this.getItemId(item) !== id);
      }
    }
  }

  /**
   * Recursively selects/deselects all children when parent is toggled.
   * Only applies when checkableSettings.checkChildren = true.
   */
  private cascadeSelectionToChildren(dataItem: T, checked: boolean): void {
    const internalItem = dataItem as InternalTreeItem<T>;
    const children = internalItem.children || [];
    
    if (children.length === 0) return;
    
    children.forEach(child => {
      if (child.__type === LOADING_ROW_TYPE) return;

      // When selecting (checked=true), skip children excluded by the caller's filter.
      // Deselection always propagates so no orphaned checked children remain.
      if (this.checkChildFilter && !this.checkChildFilter(child as T)) return;

      this.toggleItemSelection(child as T, checked);
      this.cascadeSelectionToChildren(child as T, checked);
    });
  }

  /**
   * Recursively propagates selection state up to parents.
   * - If child selected: ensure parent is selected.
   * - If child unselected: unselect parent if ALL eligible children are now unselected.
   */
  private cascadeSelectionToParents(dataItem: T, checked: boolean): void {
    const internalItem = dataItem as InternalTreeItem<T>;
    const parentId = internalItem.__parentId;
    if (parentId === undefined || parentId === null) return;

    const parent = this.nodeMap.get(parentId);
    if (!parent) return;

    if (checked) {
      // If child selected, ensure parent is selected
      if (!this.isRowSelected(parent as T)) {
        this.toggleItemSelection(parent as T, true);
      }
    } else {
      // If child unselected, check if ANY other child is still selected
      const hasSelectedChildren = parent.children?.some(child => 
        child.__type !== LOADING_ROW_TYPE && this.isRowSelected(child as T)
      );
      if (!hasSelectedChildren) {
        this.toggleItemSelection(parent as T, false);
      }
    }

    // Continue up the tree
    this.cascadeSelectionToParents(parent as T, checked);
  }

  public isRowSelected(dataItem: T): boolean {
    // Non-root items that fail the caller's filter can never appear selected,
    // regardless of whether select-all is active or the item is in selectedKeys.
    const depth = (dataItem as any).__depth ?? 0;
    
    // Explicit row disabling rule
    if (this.rowSelectionDisabled && this.rowSelectionDisabled(dataItem, depth)) {
      return false;
    }

    // Legacy child filter (selection filtering for cascade)
    if (depth > 0 && this.checkChildFilter && !this.checkChildFilter(dataItem)) {
      return false;
    }
    const id = this.getItemId(dataItem);
    const isExcluded = this.excludeIds.some(e => e.id === id);
    return this.isSelectAll ? !isExcluded : this.selectedKeys.some(k => k.id === id && k.level === depth);
  }

  // ─── Grid State handlers ──────────────────────────────────────────────────

  public onSortChange(sort: SortDescriptor[]): void {
    this.treeListSort = (sort ?? []).filter(s => s.dir != null);
    this.emitGridState();
    this.sortFilterChange.emit({ sort: this.treeListSort, filter: this.treeListFilter });
  }

  public onFilterChange(filter: CompositeFilterDescriptor): void {
    this.treeListFilter = filter ?? { ...ForecastRxTreelistComponent.EMPTY_FILTER };
    this.emitGridState();
    this.sortFilterChange.emit({
      sort: this.treeListSort,
      filter: this.treeListFilter,
    });
    this.cdr.markForCheck();
  }

  public getExtraFilterChipLabel(filterChip: CompositeFilterDescriptor): string {
    const filters = filterChip.filters as FilterDescriptor[];
    const firstFilter = filters[0];

    if (!firstFilter) {
      return '';
    }

    const column = this.columnDefs.find((col) => col.field === firstFilter.field);
    const displayName = column ? column.title : firstFilter.field;

    let label = ` ${displayName} `;

    filters.forEach((filter, idx) => {
      let filterValue = filter.value;

      const columnDef = this.columnDefs.find((col) => col.field === filter.field);
      if (columnDef?.valueMap?.length) {
        filterValue = columnDef.valueMap.find(v => v.value === filterValue)?.label || filterValue;
      }

      if (columnDef?.cellType === 'date' && filterValue) {
        filterValue = formatDate(filterValue, columnDef.dateFormat || 'MM/dd/yyyy', 'en-US');
      }

      const operator = getReadableKendoOperator(
        filter.operator as string,
        filterValue
      );
      const displayValue = this.getDisplayFilterValue(
        filter.operator as string,
        filterValue
      );

      label += `${operator} ${displayValue} ${
        idx < filters.length - 1
          ? ` ${filterChip.logic?.toUpperCase()} `
          : ''
      }`;
    });

    return label;
  }

  private getDisplayFilterValue(filterOperator: string, filterValue: any): string | number {
    if (filterOperator === 'isnull' || filterOperator === 'isnotnull') {
      return '';
    }
    return filterValue === null || filterValue === undefined ? '' : typeof filterValue === 'string' ? `${filterValue}` : filterValue;
  }

  public deleteExtraFilter(index: number): void {
    if (this.treeListFilter.filters) {
      this.treeListFilter.filters.splice(index, 1);
      this.onFilterChange(this.treeListFilter);
    }
  }

  public clearAllFilter(): void {
    this.treeListFilter = { logic: Logic.and, filters: [] };
    this.onFilterChange(this.treeListFilter);
  }

  public clearAll(): void {
    this.treeListSort = [];
    this.treeListFilter = { ...ForecastRxTreelistComponent.EMPTY_FILTER };
    this.onFilterChange(this.treeListFilter);
  }



  public onColumnVisibilityChange(event: ColumnVisibilityChangeEvent): void {
    if (!event?.columns?.length) return;
    this.columnDefs = this.mergeChangedColumns(event.columns, (def, changed) => ({
      ...def,
      visible: !(changed as KendoColVisibilityEvent).hidden,
    }));
    this.emitGridState();
    this.calcGridHeight();
    this.cdr.markForCheck();
  }

  public fullScreen(): void {
    this.isFullScreen = !this.isFullScreen;

    const actionCenter = document.querySelector('app-action-center-manual-suggestion') as HTMLElement;
    const neededToBeHiddenElements = actionCenter ? [actionCenter] : [];

    toggleFullScreen(
      this.treeListView.nativeElement,
      neededToBeHiddenElements,
      () => {
        this.headerService.isFullScreen.next(this.isFullScreen);

        // Small delay to allow headers/nav to hide before measuring 'top' position
        setTimeout(() => this.calcGridHeight(), 50);
      },
      this.isFullScreen
    );
  }

  public calcGridHeight(): void {
    if (!this.treeListView) return;

    if (this.disabledCalcGridHeight) {
      this.gridHeight = this.height;
      this.cdr.markForCheck();
      return;
    }

    const gridEl = this.treeListView.nativeElement as HTMLElement;
    const { top } = gridEl.getBoundingClientRect();

    const calculatedHeight = window.innerHeight - top - 30; // Closer buffer like DataTable

    this.gridHeight = this.isFullScreen
      ? window.innerHeight - top + 10
      : Math.max(calculatedHeight, this.height || 400);

    this.cdr.markForCheck();
  }


  public toggleColumnLock(field: string, event: MouseEvent): void {
    const col = this.columnDefs.find(c => c.field === field);
    if (col && col.lockable === false) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.columnDefs = this.columnDefs.map(def =>
      def.field === field ? { ...def, locked: !def.locked } : def,
    );
    this.emitGridState();
    this.cdr.markForCheck();
  }

  public onColumnResize(event: ColumnResizeArgs[]): void {
    const widthByField = new Map(event.map(e => [(e.column as { field?: string }).field, e.newWidth]));
    this.columnDefs = this.columnDefs.map(def => {
      const newWidth = widthByField.get(def.field);
      return newWidth != null ? { ...def, width: newWidth } : def;
    });
    this.emitGridState();
  }

  public onColumnReorder(event: ColumnReorderEvent): void {
    const reordered = [...this.columnDefs];

    // Use field name to locate the true array index — event.oldIndex/newIndex are
    // offset by the number of hardcoded columns (e.g. Checkbox) rendered before *ngFor.
    const field = (event.column as any).field as string | undefined;
    if (!field) return;

    const oldIndex = reordered.findIndex(c => c.field === field);
    if (oldIndex === -1) return;

    // offset = number of fixed columns rendered before *ngFor (1 if checkbox visible, else 0)
    const offset = this.showCheckboxColumn ? event.oldIndex - oldIndex : 0;
    const newIndex = event.newIndex - offset;

    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    this.columnDefs = reordered;
    this.emitGridState();
    this.cdr.markForCheck();
  }

  public onColumnLockedChange(event: ColumnLockedChangeEvent): void {
    if (!event?.columns?.length) return;
    this.columnDefs = this.mergeChangedColumns(event.columns, (def, changed) => ({
      ...def,
      locked: !!(changed as KendoColLockedEvent).locked,
    }));
    this.emitGridState();
    this.cdr.markForCheck();
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /** Applies a per-column mutation across `columnDefs` for the subset reported by Kendo. */
  private mergeChangedColumns(
    changedColumns: unknown[],
    merge: (def: TreelistColumnDef, changed: unknown) => TreelistColumnDef,
  ): TreelistColumnDef[] {
    const changedByField = new Map<string, unknown>(
      changedColumns.map(c => [(c as { field: string }).field, c]),
    );
    return this.columnDefs.map(def => {
      const changed = changedByField.get(def.field);
      return changed ? merge(def, changed) : def;
    });
  }

  /** Builds a GridState snapshot from the live internal state and emits it. */
  private emitGridState(): void {
    const columns = this.columnDefs.map(def => {
      const column = new GridStateColumn();
      column.name = def.field;
      column.field = def.field;
      column.visible = def.visible ?? true;
      column.locked = !!def.locked;
      column.width = def.width != null ? String(def.width) : '*';
      return column;
    });

    const gridState = new GridState();
    gridState.sort = this.treeListSort;
    gridState.filter = this.treeListFilter;
    gridState.columns = columns;
    gridState.pagination = new GridStatePagination({
      paginationCurrentPage: 0,
      paginationPageSize: this.pageSize,
    });

    this.stateChange.emit(gridState);
  }

  private getItemId(item: T | InternalTreeItem<T>): TreeKey {
    const internalItem = item as InternalTreeItem<T>;
    const depth = internalItem.__depth ?? 0;
    
    // Use idFieldsByDepth if provided and depth is within bounds
    const fieldName = this.idFieldsByDepth.length > 0 && this.idFieldsByDepth[depth] 
      ? this.idFieldsByDepth[depth] 
      : this.idField;
    
    const rawId = (item as Record<string, unknown>)[fieldName];
    if (rawId !== undefined && rawId !== null) {
      return typeof rawId === 'string' || typeof rawId === 'number' ? rawId : String(rawId);
    }
    
    // Fallback to idField if specified field doesn't exist
    if (fieldName !== this.idField) {
      const fallbackId = (item as Record<string, unknown>)[this.idField];
      if (fallbackId !== undefined && fallbackId !== null) {
        return typeof fallbackId === 'string' || typeof fallbackId === 'number' ? fallbackId : String(fallbackId);
      }
    }
    
    console.warn(`ForecastRxTreelist: item at depth ${depth} has no id field "${fieldName}"`);
    return `__missing_${depth}_${Math.random()}`;
  }

  /** Projects saved column state (visible, locked, width, order) back onto `columnDefs`. */
  private applyColumnStateFromSaved(): void {
    const savedColumns = this.savedGridState?.columns;
    if (!savedColumns?.length || !this.columnDefs?.length) return;

    const savedByField = new Map<string, GridStateColumn>(
      savedColumns
        .filter(c => !!c?.field || !!c?.name)
        .map(c => [c.field ?? c.name, c]),
    );

    // Restore column order from saved state; append any new columns not in saved state.
    const savedFieldOrder = savedColumns.map(c => c.field ?? c.name).filter(Boolean);
    const unsaved = this.columnDefs.filter(def => !savedByField.has(def.field));
    const ordered: TreelistColumnDef[] = [
      ...savedFieldOrder
        .map(field => this.columnDefs.find(def => def.field === field))
        .filter((def): def is TreelistColumnDef => def != null),
      ...unsaved,
    ];

    this.columnDefs = ordered.map(def => {
      const saved = savedByField.get(def.field);
      if (!saved) return def;
      const savedWidth = saved.width && saved.width !== '*' ? Number(saved.width) : null;
      return {
        ...def,
        visible: saved.visible ?? def.visible ?? true,
        locked: saved.locked ?? def.locked ?? false,
        ...(savedWidth != null ? { width: savedWidth } : {}),
      };
    });
  }

  private setChildren(parentId: TreeKey, children: T[]): void {
    const parent = this.nodeMap.get(parentId);
    if (!parent) return;

    const parentDepth: number = parent.__depth ?? 0;
    const mapped = children.map(c => ({
      ...c,
      __depth: parentDepth + 1,
      __parentId: parentId,
      children: [],
    } as InternalTreeItem<T>));

    mapped.forEach(c => this.nodeMap.set(this.getItemId(c), c));
    parent.children = mapped;
    this.rootItemsList = [...this.rootItemsList];
    this.cdr.markForCheck();
  }

  private createLoadingRow(parentId: TreeKey): InternalTreeItem<T> {
    const row: Record<string, unknown> = {
      __type: LOADING_ROW_TYPE,
      __parentId: parentId,
    };
    row[this.idField] = `__loading_${parentId}`;
    return row as InternalTreeItem<T>;
  }

}
